import { ReactElement, useCallback } from 'react';
import { useInternationalization } from "@progress/kendo-react-intl"
import { useTableKeyboardNavigation } from '@progress/kendo-react-data-tools';
import { GridCustomCellProps, GRID_COL_INDEX_ATTRIBUTE } from '@progress/kendo-react-grid';
import { classNames } from '@progress/kendo-react-common';

type CustomGridCellProps = {
    title?: string
} & GridCustomCellProps;

function getNestedValue(fieldName: string, dataItem: any) {
    const path = (fieldName || '').split('.');
    let data = dataItem;
    path.forEach((p: any) => {
        data = data ? data[p] : undefined;
    });
  
    return data;
}

export const CustomGridCell = (props: CustomGridCellProps) => {
    let content = props.children;
    let defaultRendering: ReactElement<HTMLTableCellElement> | null = null;
    let tdProps = null;
    const intl = useInternationalization();
    const navigationAttributes = useTableKeyboardNavigation(props.id);
    
    const onClick = (event: any) => {
        if (event.currentTarget !== event.target) {
            event.currentTarget.click();
        }
    };

    const onContextMenu = useCallback(
        (event: any) => {
            if (props.onContextMenu) {
                props.onContextMenu.call(undefined, event, props.dataItem, props.field);
            }
        },
        [props.onContextMenu, props.dataItem, props.field]
    );

    if (props.rowType === 'groupFooter') {
        defaultRendering = (
            <td className={props.className} onContextMenu={onContextMenu} />
        ) as React.ReactElement<HTMLTableCellElement>;
    } else if (props.field !== undefined && props.rowType !== 'groupHeader') {
        const data = getNestedValue(props.field, props.dataItem);

        if (!content && data !== undefined && data !== null) {
            content = props.format
                ? intl.format(props.format, data)
                : data.toString();
        }

        const className = classNames('k-table-td', props.className, {
            'k-selected': props.isSelected
        });

        tdProps = {
            onClick,
            onContextMenu,
            colSpan: props.colSpan,
            style: props.style,
            className,
            role: 'gridcell',
            'aria-colindex': props.ariaColumnIndex,
            'aria-selected': props.isSelected,
            [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex,
            ...navigationAttributes
        };

        defaultRendering = (
            <td {...tdProps}>
                {props.title
                    ? (
                        <>
                            <span className="cell-heading">{props.title}</span>
                            <span className="cell-value">{content}</span>
                        </>
                    )
                    : content
                }
            </td>
        );

    } 

    const rowTypeSetting = props.rowType || 'data';
    const customCells = props.cells;
    if (customCells && customCells[rowTypeSetting]) {
        const CustomCell: any = customCells[rowTypeSetting];
        return (
            <CustomCell {...props} tdProps={tdProps}>
                {content}
            </CustomCell>
        );
    }

    return props.render
        ? props.render.call(undefined, defaultRendering, props)
        : defaultRendering;
}